/**
 * RentEstimationSection Component (Phase 156-03)
 *
 * Wireframe-compliant rent estimation section with 3 parts:
 * 1. Source table with gold header row
 * 2. Side-by-side As-Is vs Post-Renovation cards
 * 3. Summary callout bar
 *
 * Replaces the amber-themed VacantRentEstimationCard.
 * Uses luxury dark theme (charcoal/gold/stone) consistent with Tab1.
 */

import { TrendingUp, Check } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import type { RentSource } from '../../types/calculations';

export interface RentEstimationSectionProps {
  rentSources: RentSource[];
  blendedRent: number;          // Blended as-is rent per unit/month
  stabilizedRent: number;       // Post-renovation rent per unit/month
  totalUnits: number;
  vacancyRate: number;          // e.g., 0.07
  opexRate: number;             // e.g., 0.425
  marketValue: number;          // For cap rate calc
  confidence: string;
  capexBudget?: number;         // For "from $45K CapEx" label
  apiNoiUplift?: number;        // Phase 167.1: authoritative NOI uplift from API (rent_capex_transparency.annual_noi_uplift)
}

function getConfidenceBadgeClass(confidence: string): string {
  switch (confidence?.toUpperCase()) {
    case 'HIGH':
      return 'text-emerald bg-emerald/10 border-emerald/30';
    case 'MEDIUM':
      return 'text-gold bg-gold/10 border-gold/30';
    case 'LOW':
      return 'text-crimson bg-crimson/10 border-crimson/30';
    default:
      return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
  }
}

export function RentEstimationSection({
  rentSources,
  blendedRent,
  stabilizedRent,
  totalUnits,
  vacancyRate,
  opexRate,
  marketValue,
  confidence,
  capexBudget,
  // apiNoiUplift unused — NOI uplift block consolidated to CapExBreakdown (PUAT-02)
}: RentEstimationSectionProps) {
  if (!blendedRent || blendedRent <= 0) {
    return null;
  }

  const units = totalUnits || 1;

  // As-Is calculations
  const asIsGPR = blendedRent * units * 12;
  const asIsEGI = asIsGPR * (1 - vacancyRate);
  const asIsNOI = asIsEGI * (1 - opexRate);
  const asIsCapRate = marketValue > 0 ? (asIsNOI / marketValue) : 0;

  // Post-Renovation calculations
  const postRenoGPR = stabilizedRent * units * 12;
  const postRenoEGI = postRenoGPR * (1 - vacancyRate);
  const postRenoNOI = postRenoEGI * (1 - opexRate);
  const postRenoCapRate = marketValue > 0 ? (postRenoNOI / marketValue) : 0;

  // Uplift deltas
  const rentUplift = stabilizedRent - blendedRent;
  const rentUpliftPct = blendedRent > 0 ? (rentUplift / blendedRent) * 100 : 0;
  const hasUplift = rentUplift > 0;

  const vacancyPct = (vacancyRate * 100).toFixed(0);
  const opexPct = (opexRate * 100).toFixed(1);

  return (
    <div className="space-y-4">
      {/* ================================================================
          PART 1: Source Table
         ================================================================ */}
      <div className="overflow-hidden rounded-lg border border-slate-dark">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gold/15 border-b border-gold/30">
              <th className="text-left py-2.5 px-3 font-mono tabular-nums font-semibold text-gold uppercase tracking-wider text-sm">
                Source
              </th>
              <th className="text-right py-2.5 px-3 font-mono tabular-nums font-semibold text-gold uppercase tracking-wider text-sm">
                Estimate
              </th>
              <th className="text-center py-2.5 px-3 font-mono tabular-nums font-semibold text-gold uppercase tracking-wider text-sm">
                Confidence
              </th>
              <th className="text-center py-2.5 px-3 font-mono tabular-nums font-semibold text-gold uppercase tracking-wider text-sm">
                Selected
              </th>
              <th className="text-left py-2.5 px-3 font-mono tabular-nums font-semibold text-gold uppercase tracking-wider text-sm">
                Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {rentSources.map((src, idx) => (
              <tr
                key={idx}
                className={`border-b border-slate-dark/50 ${
                  src.selected ? 'bg-gold/5' : idx % 2 === 0 ? 'bg-charcoal/20' : 'bg-charcoal/40'
                }`}
              >
                <td className="py-2 px-3 text-stone-300 font-medium">
                  {src.name}
                </td>
                <td className="py-2 px-3 text-right font-mono tabular-nums text-ivory">
                  {src.value > 0 ? formatCurrency(src.value) : 'N/A'}
                </td>
                <td className="py-2 px-3 text-center">
                  {src.confidence && (
                    <span className={`inline-block px-2 py-0.5 rounded text-sm font-mono tabular-nums font-semibold uppercase border ${getConfidenceBadgeClass(src.confidence)}`}>
                      {src.confidence}
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-center">
                  {src.selected && (
                    <Check className="w-3.5 h-3.5 text-emerald mx-auto" />
                  )}
                </td>
                <td className="py-2 px-3 text-stone-700 max-w-[200px] truncate">
                  {src.source || '---'}
                </td>
              </tr>
            ))}
            {/* Blended row */}
            <tr className="bg-gold/5 border-t border-gold/30">
              <td className="py-2.5 px-3 font-semibold text-gold">
                Blended As-Is
              </td>
              <td className="py-2.5 px-3 text-right font-mono tabular-nums font-bold text-gold">
                {formatCurrency(blendedRent)}
              </td>
              <td className="py-2.5 px-3 text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-sm font-mono tabular-nums font-semibold uppercase border ${getConfidenceBadgeClass(confidence)}`}>
                  {confidence}
                </span>
              </td>
              <td className="py-2.5 px-3" />
              <td className="py-2.5 px-3 text-stone-700 text-sm">
                {rentSources.length} sources evaluated
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ================================================================
          PART 2: Side-by-Side Rent Cards
         ================================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* As-Is Card */}
        <div className="rounded-lg border border-slate-dark bg-charcoal/40 p-5">
          <div className="text-sm uppercase tracking-wider text-stone-700 font-mono tabular-nums font-semibold mb-4">
            As-Is Rents (Current Condition)
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-stone-700 text-sm">Rent / Unit / Month</span>
              <span className="font-mono tabular-nums text-lg font-bold text-ivory">
                {formatCurrency(blendedRent)}
              </span>
            </div>
            <div className="border-t border-slate-dark/50 pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  GPR ({units} unit{units > 1 ? 's' : ''} x 12 mo)
                </span>
                <span className="font-mono tabular-nums text-stone-300">
                  {formatCurrency(asIsGPR)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  EGI (after {vacancyPct}% vac)
                </span>
                <span className="font-mono tabular-nums text-stone-300">
                  {formatCurrency(asIsEGI)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  NOI (at {opexPct}% OpEx)
                </span>
                <span className="font-mono tabular-nums text-ivory font-semibold">
                  {formatCurrency(asIsNOI)}
                </span>
              </div>
              {marketValue > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-stone-700">
                    Cap Rate (at {formatCurrency(marketValue)})
                  </span>
                  <span className="font-mono tabular-nums text-stone-300">
                    {(asIsCapRate * 100).toFixed(2)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Post-Renovation Card */}
        <div className={`rounded-lg p-5 ${
          hasUplift
            ? 'border border-gold bg-gold/5'
            : 'border border-slate-dark bg-charcoal/40'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm uppercase tracking-wider text-stone-700 font-mono tabular-nums font-semibold">
              Post-Renovation Rents (Stabilized)
            </div>
            {hasUplift && (
              <TrendingUp className="w-4 h-4 text-emerald" />
            )}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-stone-700 text-sm">Rent / Unit / Month</span>
              <span className="font-mono tabular-nums text-lg font-bold text-gold">
                {formatCurrency(stabilizedRent)}
              </span>
            </div>
            {hasUplift && (
              <div className="text-sm font-mono tabular-nums text-emerald">
                Uplift: +{formatCurrency(rentUplift)}/unit (+{rentUpliftPct.toFixed(1)}%)
                {capexBudget != null && capexBudget > 0 && (
                  <span className="text-stone-700 ml-2">
                    from {formatCurrency(capexBudget)} CapEx
                  </span>
                )}
              </div>
            )}
            <div className="border-t border-slate-dark/50 pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  GPR ({units} unit{units > 1 ? 's' : ''} x 12 mo)
                </span>
                <span className="font-mono tabular-nums text-stone-300">
                  {formatCurrency(postRenoGPR)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  EGI (after {vacancyPct}% vac)
                </span>
                <span className="font-mono tabular-nums text-stone-300">
                  {formatCurrency(postRenoEGI)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-700">
                  NOI (at {opexPct}% OpEx)
                </span>
                <span className="font-mono tabular-nums text-ivory font-semibold">
                  {formatCurrency(postRenoNOI)}
                </span>
              </div>
              {marketValue > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-stone-700">
                    Cap Rate (at {formatCurrency(marketValue)})
                  </span>
                  <span className="font-mono tabular-nums text-stone-300">
                    {(postRenoCapRate * 100).toFixed(2)}%
                  </span>
                </div>
              )}
            </div>

            {/* NOI uplift block removed — consolidated to CapExBreakdown inline stat (PUAT-02) */}
          </div>
        </div>
      </div>

      {/* ================================================================
          PART 3: Summary Callout
         ================================================================ */}
      {hasUplift && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-gold/5 border-l-2 border-gold">
          <div className="text-sm text-stone-700 leading-relaxed">
            As-Is NOI: <span className="font-mono tabular-nums font-bold text-ivory">{formatCurrency(asIsNOI)}</span>
            <span className="mx-2 text-stone-700">|</span>
            Uplifted NOI: <span className="font-mono tabular-nums font-bold text-gold">{formatCurrency(postRenoNOI)}</span>
            <span className="mx-2 text-stone-700">|</span>
            Offer prices above use <span className="font-semibold text-gold">uplifted (stabilized)</span> rents
            <span className="text-stone-700"> -- assumes CapEx completed within Year 1</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default RentEstimationSection;
